import { check401, getValidAccessToken } from "@utils/authenticatedFetch";
import { getApiOrigin } from "@utils/apiOrigin";

/**
 * Vertical-aware facility search.
 *
 * The CMS NPI registry only knows healthcare providers, so the existing
 * /api/v1/npi/lookup/organization endpoint returns 0 (or worse, irrelevant
 * healthcare matches) for verticals like food_service, aviation_*, or
 * contracts. This module wraps the Rules server's
 * POST /api/v1/facility-management/lookup-by-vertical endpoint. Aviation and
 * food_service use live source resolvers; pharma_manufacturing uses FDA
 * DECRS + EPA ECHO (``fda_establishment``). Other verticals still search the
 * local facilities table filtered by Facility.vertical.
 *
 * The response intentionally mirrors the shape of NPI org-lookup results
 * (npi_number=null) so the Hub can reuse the existing facility-card UI in
 * the non-healthcare add-facility screen without per-vertical branching.
 */

/** Use with `vertical: "aviation"` so name/IATA search hits FAA NASR airports, not profile-based feeds. */
export const FAA_NASR_AIRPORTS_LOOKUP_SOURCE = "faa_nasr_airports";

/** Use with `vertical: "pharma_manufacturing"` so search hits FDA DECRS + EPA ECHO, not the local facility table. */
export const FDA_ESTABLISHMENT_LOOKUP_SOURCE = "fda_establishment";

export interface VerticalFacilityLookupRequest {
  /** Required vertical slug — e.g. "food_service", "aviation". */
  vertical: string;
  /** Required name anchor for the search (substring, case-insensitive). */
  name: string;
  /** Optional 2-letter US state filter (case-insensitive). */
  state?: string;
  /** Optional city substring filter. */
  city?: string;
  /** Optional phone digits-only fuzzy match. */
  phone?: string;
  /** Optional contact email substring filter. */
  email?: string;
  /** Aviation wizard profile slug used to route FAA/DOT resolvers. */
  primary_profile_slug?: string;
  /** Aviation wizard activity domains used as fallback routing hints. */
  activity_domains?: string[];
  /** Optional explicit aviation source override. */
  lookup_source?: string;
  /** Structured targeting for profile-driven non-healthcare verticals. */
  profile_targeting_json?: Record<string, unknown>;
  /** ISO 3166-1 alpha-3 country code. Defaults server-side to "USA". */
  country_code?: string;
}

/**
 * Address sub-shape mirrors NPIPrimaryAddress so existing card components
 * can render it without code changes.
 */
export interface VerticalFacilityAddress {
  address_1?: string | null;
  address_2?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | null;
  country_code?: string | null;
  telephone_number?: string | null;
  fax_number?: string | null;
}

export interface VerticalFacilityType {
  id?: number | null;
  type_slug?: string | null;
  display_name?: string | null;
  category?: string | null;
  vertical?: string | null;
  requires_medicare_certification?: boolean | null;
}

export interface VerticalFacilityRecord {
  id?: number | null;
  facility_unique_id?: string | null;
  facility_name?: string | null;
  vertical?: string | null;
  primary_profile_slug?: string | null;
  profile_targeting_json?: Record<string, unknown> | null;
  is_verified?: boolean | null;
  is_active?: boolean | null;
}

export interface VerticalFacilityResult {
  /** Always null for non-healthcare; the field exists to mirror NPI shape. */
  npi_number?: string | null;
  /** Always null for non-healthcare. */
  enumeration_type?: string | null;
  /** Display name (mirrors NPI site_name). */
  site_name?: string | null;
  organization_name?: string | null;
  primary_address?: VerticalFacilityAddress | null;
  primary_taxonomy?: null;
  facility_type?: VerticalFacilityType | null;
  /** Always true here — the type came from the local DB row, not inference. */
  facility_type_confirmed?: boolean | null;
  /** The underlying Facility row, useful for stamping vertical on create. */
  facility?: VerticalFacilityRecord | null;
  vertical?: string | null;
  source?: string | null;
  source_label?: string | null;
  source_url?: string | null;
  source_refreshed_at?: string | null;
  external_id?: string | null;
  authority_type?: string | null;
  certificate_or_location_id?: string | null;
  primary_profile_slug?: string | null;
  profile_targeting_json?: Record<string, unknown> | null;
}

export interface VerticalFacilityLookupData {
  vertical: string;
  query: {
    name: string;
    state?: string | null;
    city?: string | null;
    phone?: string | null;
    email?: string | null;
    country_code?: string | null;
  };
  search_backend:
    | "local_facility_db"
    | "aviation_source_resolvers"
    | "food_service_source_resolvers"
    | "fda_establishment_resolvers";
  sources?: string[];
  unavailable_sources?: Array<{ source: string; reason: string }>;
  result_count: number;
  results: VerticalFacilityResult[];
}

export interface VerticalFacilityLookupResponse {
  success: boolean;
  message?: string;
  data?: VerticalFacilityLookupData | null;
  error?: { code?: string; message?: string } | null;
}

/**
 * Call the Rules server's vertical-aware facility lookup.
 *
 * Returns the parsed JSON envelope. Network/HTTP errors throw; soft
 * failures (`success: false`) are returned to the caller so the UI can
 * differentiate between "no results" (200 with empty list) and explicit
 * server-side rejections (e.g. attempting to search the healthcare
 * vertical via this endpoint, which is a 409).
 */
export async function lookupFacilitiesByVertical(
  payload: VerticalFacilityLookupRequest,
): Promise<VerticalFacilityLookupResponse> {
  const baseURL = getApiOrigin();
  const token = getValidAccessToken();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const body: Record<string, unknown> = {
    vertical: payload.vertical,
    name: payload.name,
  };
  const state = (payload.state ?? "").trim();
  const city = (payload.city ?? "").trim();
  const phone = (payload.phone ?? "").trim();
  const email = (payload.email ?? "").trim();
  const primary_profile_slug = (payload.primary_profile_slug ?? "").trim();
  const lookup_source = (payload.lookup_source ?? "").trim();
  const country_code = (payload.country_code ?? "").trim();
  if (state) body.state = state.toUpperCase();
  if (city) body.city = city;
  if (phone) body.phone = phone;
  if (email) body.email = email;
  if (primary_profile_slug) body.primary_profile_slug = primary_profile_slug;
  if (payload.activity_domains?.length) body.activity_domains = payload.activity_domains;
  if (lookup_source) body.lookup_source = lookup_source;
  if (payload.profile_targeting_json) body.profile_targeting_json = payload.profile_targeting_json;
  if (country_code) body.country_code = country_code.toUpperCase();

  const response = await fetch(
    `${baseURL}/api/v1/facility-management/lookup-by-vertical`,
    {
      method: "POST",
      headers,
      body: JSON.stringify(body),
    },
  );

  check401(response);

  // 409 = healthcare vertical sent here; 400 = missing required field.
  // Both are predictable user-correctable cases — surface as soft failure
  // so the screen can show a friendly message without an error toast.
  if (response.status === 409 || response.status === 400) {
    let detail: string | undefined;
    try {
      const errBody = await response.json();
      detail = typeof errBody?.detail === "string" ? errBody.detail : undefined;
    } catch {
      detail = undefined;
    }
    return {
      success: false,
      message:
        detail ??
        (response.status === 409
          ? "Healthcare vertical must use NPI lookup."
          : "Vertical or facility name is required."),
      data: null,
      error: {
        code: response.status === 409
          ? "HEALTHCARE_VERTICAL_NOT_SUPPORTED"
          : "BAD_REQUEST",
      },
    };
  }

  if (!response.ok) {
    throw new Error(
      `Vertical facility lookup failed with HTTP ${response.status}`,
    );
  }

  const data = (await response.json()) as VerticalFacilityLookupResponse;
  return data;
}
